import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { MobileShell } from "@/components/MobileShell";
import { useAuthSession } from "@/context/AuthSessionContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const EMAIL_KEY = "storefront_pending_login_identifier";
const REDIRECT_KEY = "storefront_pending_login_redirect";

export default function AccountVerify() {
  const navigate = useNavigate();
  const location = useLocation();
  const { verifyOtp, sendOtp } = useAuthSession();
  const [identifier, setIdentifier] = useState(location.state?.identifier || sessionStorage.getItem(EMAIL_KEY) || "");
  const redirectTo = location.state?.redirectTo || sessionStorage.getItem(REDIRECT_KEY) || "/account";
  const [otpCode, setOtpCode] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [verified, setVerified] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(30);
  const [resendPending, setResendPending] = useState(false);
  const lastOtpAttemptRef = useRef("");

  useEffect(() => {
    if (!identifier) {
      navigate("/account/email", { replace: true });
    }
  }, [identifier, navigate]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (!/^\d{6}$/.test(otpCode)) return;
    if (pending || verified || lastOtpAttemptRef.current === otpCode) return;

    const runVerify = async () => {
      setPending(true);
      setError("");
      lastOtpAttemptRef.current = otpCode;
      try {
        await verifyOtp({ identifier: identifier.trim(), verificationCode: otpCode });
        sessionStorage.removeItem(EMAIL_KEY);
        sessionStorage.removeItem(REDIRECT_KEY);
        setVerified(true);
        toast.success("Login successful.");
        navigate(redirectTo, { replace: true });
      } catch (err) {
        setError(err?.response?.data?.detail || "Invalid verification code.");
        setOtpCode("");
        lastOtpAttemptRef.current = "";
        toast.error(err?.response?.data?.detail || "Could not verify code");
      } finally {
        setPending(false);
      }
    };

    runVerify();
  }, [otpCode, identifier, navigate, pending, redirectTo, verified, verifyOtp]);

  const handleResendCode = async () => {
    if (!identifier || resendCooldown > 0 || resendPending) return;

    setResendPending(true);
    try {
      await sendOtp(identifier.trim());
      setOtpCode("");
      setError("");
      lastOtpAttemptRef.current = "";
      setResendCooldown(30);
      toast.success("A new verification code was sent.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send a new code");
    } finally {
      setResendPending(false);
    }
  };

  return (
    <MobileShell showCart={false}>
      <div className="pt-6 md:pt-10 max-w-xl mx-auto space-y-6" data-testid="account-verify-page">
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Account access</p>
          <h1 className="font-serif text-4xl md:text-5xl">Enter the code</h1>
          <p className="text-sm text-muted-foreground">
            We sent a 6-digit code to <span className="text-foreground">{identifier}</span>.
          </p>
          <p className="text-xs text-muted-foreground italic">
            Can't find it? Please check your spam or junk folder just in case.
          </p>
        </div>

        <div className="rounded-3xl border border-border/60 bg-surface/30 p-5 space-y-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-[0.15em] text-foreground">
              Verification code
            </Label>
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={(value) => {
                setOtpCode(value.replace(/\D/g, "").slice(0, 6));
                setError("");
              }}
              disabled={pending || verified}
              containerClassName="mt-2"
            >
              <InputOTPGroup className="w-full justify-between">
                {[0, 1, 2, 3, 4, 5].map((index) => (
                  <InputOTPSlot
                    key={index}
                    index={index}
                    className={`h-12 w-12 rounded-xl border ${error ? "border-destructive" : verified ? "border-moss" : "border-input"}`}
                  />
                ))}
              </InputOTPGroup>
            </InputOTP>
            {pending && (
              <p className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Verifying code...
              </p>
            )}
            {verified && (
              <p className="mt-2 text-xs text-moss inline-flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> Login verified
              </p>
            )}
            {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Need a new code?{" "}
            <button
              type="button"
              disabled={resendCooldown > 0 || resendPending}
              onClick={handleResendCode}
              className="text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendPending ? "Sending..." : `Send new code${resendCooldown > 0 ? ` (${resendCooldown}s)` : ""}`}
            </button>
          </p>
        </div>
      </div>
    </MobileShell>
  );
}